import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AppConfigService } from '@app/services/config/app-config.service';
import { SessionKeys } from '@app/constants/session-keys';
import {
  Buttons,
  ConfirmationMessage,
  IDialogResult,
} from '@app/components/shared/confirmation-dialog/model/dialog-models';
import { ConfirmationDialogComponent } from '@app/components/shared/confirmation-dialog/confirmation-dialog.component';

import { DeploymentService } from '@app/rest-client/faxe/services/deployment.service';
import { take } from 'rxjs';
import { AppUpdateService } from '@app/services/app-update/app-update.service';

@Component({
  selector: 'faxe-banner',
  templateUrl: './banner.component.html',
  styleUrls: ['./banner.component.scss'],
  imports: [],
})
export class BannerComponent implements OnInit {
  viewMode: boolean;

  constructor(
    private router: Router,
    private confirmationDialog: MatDialog,
    private deploymentService: DeploymentService,
    private appUpdateService: AppUpdateService
  ) {}

  ngOnInit(): void {
    this.setVeiwMode(sessionStorage.getItem(SessionKeys.viewMode));
  }

  onAbout(): void {
    const formattedDateTime = this.formatDeploymentDateTime(
      AppConfigService.deploymentConfigData.deploymentDateTime
    );
    const bullets = ['FE v. ' + formattedDateTime];

    this.deploymentService
      .deployVersionGet()
      .pipe(take(1))
      .subscribe((deployVersion) => {
        if (deployVersion) {
          const formattedBEDateTime = this.formatBEDeploymentDateTime(
            deployVersion
          );
          bullets.push('BE v. ' + formattedBEDateTime);
        }
        this.openConfirm(bullets);
      });
  }

  private formatDeploymentDateTime(dateTimeString?: string): string {
    if (!dateTimeString) {
      return 'okänd';
    }

    try {
      // Parse datetime string in format like "20260203T232058+01:00"
      const year = dateTimeString.substring(0, 4);
      const month = dateTimeString.substring(4, 6);
      const day = dateTimeString.substring(6, 8);
      const hour = dateTimeString.substring(9, 11);
      const minute = dateTimeString.substring(11, 13);
      const second = dateTimeString.substring(13, 15);
      const offset = dateTimeString.substring(15); // Get timezone offset like "+01:00"

      // Create ISO 8601 string with timezone
      const isoString = `${year}-${month}-${day}T${hour}:${minute}:${second}${offset}`;
      const date = new Date(isoString);

      return this.formatDateWithAge(date);
    } catch (error) {
      // Fallback to original string if parsing fails
      return dateTimeString;
    }
  }

  private formatBEDeploymentDateTime(dateTimeString?: string): string {
    if (!dateTimeString) {
      return 'okänd';
    }

    try {
      // Parse datetime string in ISO 8601 format like "2026-02-01T20:02:26+01:00"
      const date = new Date(dateTimeString);
      return this.formatDateWithAge(date);
    } catch (error) {
      // Fallback to original string if parsing fails
      return dateTimeString;
    }
  }

  private formatDateWithAge(date: Date): string {
    // Format as readable Swedish date and time, ensuring Stockholm timezone
    const formatted = date.toLocaleString('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'Europe/Stockholm',
    });

    // Add age calculation
    const age = this.calculateAge(date);
    return `${formatted} (Ålder: ${age})`;
  }

  private calculateAge(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();

    // Handle future dates (e.g., due to clock skew)
    if (diffMs < 0) {
      return '0:00:00';
    }

    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${days}:${hours
      .toString()
      .padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  setVeiwMode(value: string) {
    this.viewMode = value === null ? false : value === 'true';
  }

  home(): void {
    this.router.navigate(['/']);
  }

  openConfirm = (
    bullets: string[]
  ): MatDialogRef<
    ConfirmationDialogComponent<IDialogResult>,
    IDialogResult
  > => {
    const message: ConfirmationMessage = {} as ConfirmationMessage;
    message.title = 'Om Faxe';
    message.message =
      'Hjälp oss göra Faxe bättre genom att skicka förbättringsförslag till ' +
      'feedback@faxeit.se. Tack för din medverkan!';
    message.bulletList = bullets;
    message.primaryButton = Buttons.stang;
    message.customActionButton = 'Sök efter uppdateringar';
    message.customActionCallback = () => this.onCheckForUpdates();
    return this.confirmationDialog.open(ConfirmationDialogComponent, {
      data: message,
    });
  };

  onCheckForUpdates(): void {
    this.appUpdateService.checkForUpdate();
  }
}
